import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { unstable_batchedUpdates } from 'react-dom'
import { getMembershipMeta, getMembershipOrderSegments, getMembershipSummary } from './membershipFunnelApi'
import { describeError, getAvailability, getRangeIssue } from './membershipFunnelUtils'

const initialResource = { data: null, loading: true, error: '' }

export default function useMembershipFunnel() {
    const [meta, setMeta] = useState(null)
    const [metaError, setMetaError] = useState('')
    const [metaLoading, setMetaLoading] = useState(true)
    const [notice, setNotice] = useState('')
    const [query, setQuery] = useState({ range: '7d', platform: 'ALL', entrySource: 'ALL' })
    const [summary, setSummary] = useState(initialResource)
    const [orders, setOrders] = useState(initialResource)
    const mounted = useRef(false)
    const metaRequest = useRef(null)
    const mismatch = useRef(null)
    const queryRef = useRef(query)
    queryRef.current = query

    const refreshMeta = useCallback((resetSource = false) => {
        if (metaRequest.current) return metaRequest.current
        setMetaLoading(true)
        setMetaError('')
        const request = getMembershipMeta().then(result => {
            // Release before publishing: a fast summary may immediately request newer metadata.
            metaRequest.current = null
            if (!mounted.current) return
            const selected = queryRef.current.entrySource
            const sourceRemoved = selected !== result.allOption.value && !result.entrySourceOptions.some(row => row.entrySource === selected)
            // React 17 does not batch promise callbacks. Publish the registry and selection together.
            unstable_batchedUpdates(() => {
                setMetaLoading(false)
                setMeta({ ...result })
                if (resetSource || sourceRemoved) {
                    setQuery(current => ({ ...current, entrySource: result.allOption.value }))
                    setNotice('선택한 진입점이 변경되었습니다. 전체 진입점으로 전환했으니 목록에서 다시 선택해 주세요.')
                }
            })
        }).catch(error => {
            metaRequest.current = null
            if (mounted.current) {
                setMetaError(describeError(error))
                setMetaLoading(false)
            }
        })
        metaRequest.current = request
        return request
    }, [])

    useEffect(() => {
        mounted.current = true
        refreshMeta()
        // Returning to this screen also discovers registry changes made by operators.
        const onFocus = () => refreshMeta()
        window.addEventListener('focus', onFocus)
        return () => {
            mounted.current = false
            window.removeEventListener('focus', onFocus)
        }
    }, [refreshMeta])

    const { range, platform, entrySource, from, to } = query
    const summaryQuery = useMemo(() => ({ range, platform, entrySource, from, to }), [range, platform, entrySource, from, to])
    const orderQuery = useMemo(() => ({ range, platform, from, to }), [range, platform, from, to])

    useEffect(() => {
        if (!meta) return
        let active = true
        const error = getRangeIssue(getAvailability(meta, platform, entrySource), summaryQuery)
        setSummary({ data: null, loading: !error, error })
        if (error) return
        getMembershipSummary(summaryQuery).then(data => {
            if (!active) return
            if (data.meta.registryVersion !== meta.registryVersion) {
                const pair = `${meta.registryVersion}:${data.meta.registryVersion}`
                setSummary({ data: null, loading: false, error: '진입점 설정이 변경되어 분석 정보를 갱신합니다. 계속 표시되면 새로고침해 주세요.' })
                if (mismatch.current !== pair) {
                    mismatch.current = pair
                    setNotice('진입점 설정이 변경되어 필터 목록과 제공 기간을 갱신했습니다.')
                    refreshMeta()
                }
                return
            }
            mismatch.current = null
            setSummary({ data, loading: false, error: '' })
        }).catch(error => {
            if (!active) return
            setSummary({ data: null, loading: false, error: describeError(error) })
            if (entrySource !== meta.allOption.value && error?.response?.data?.code === 'INVALID_FILTER' && error.response.data.details?.field === 'entrySource') refreshMeta(true)
        })
        return () => { active = false }
    }, [meta, platform, entrySource, summaryQuery, refreshMeta])

    useEffect(() => {
        if (!meta) return
        let active = true
        const error = getRangeIssue(getAvailability(meta, platform), orderQuery)
        setOrders({ data: null, loading: !error, error })
        if (error) return
        getMembershipOrderSegments(orderQuery).then(data => {
            if (active) setOrders({ data, loading: false, error: '' })
        }).catch(error => {
            if (active) setOrders({ data: null, loading: false, error: describeError(error) })
        })
        return () => { active = false }
    }, [meta, platform, orderQuery])

    return { meta, metaError, metaLoading, notice, query, setQuery, summary, orders, refresh: () => refreshMeta() }
}
